import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import Select from 'react-select'

import type { ReactElement, ReactNode } from 'react'
import type { ActionMeta, MenuProps, OptionProps, Props as SelectProps, StylesConfig } from 'react-select'
import type { IOptionWithIcon } from '~/types/option'

import { Menu, Control, Option, ValueContainer } from './components'

import InputWrapper from '../InputWrapper'

import './styles.scss'

const styles: StylesConfig = {
  container: (styles) => ({
    ...styles,
    outline: 'none',
    width: '100%',
  }),
  control: (styles) => ({
    ...styles,
    '&:focus-within': {
      border: 0,
      boxShadow: 'none',
      overflow: 'hidden',
    },
    '&:hover': {
      border: 0,
      boxShadow: 'none',
    },
    backgroundColor: '#f9f9f9',
    border: 0,
    borderRadius: 5,
    cursor: 'pointer',
    maxHeight: 200,
    minHeight: 0,
    position: 'relative',
    width: '100%',
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    display: 'none',
  }),
  indicatorsContainer: (styles) => ({
    ...styles,
    height: '100%',
    position: 'sticky',
  }),
  input: (styles) => ({
    ...styles,
    backgroundColor: '#f9f9f9',
    color: '#1b1d1f',
    fontSize: '1.4rem',
    fontWeight: 500,
    width: '100%',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: '#fff',
    border: '0.1rem solid #626262',
    borderRadius: 5,
    maxWidth: 300,
    minWidth: '100%',
    zIndex: 99,
  }),
  menuList: (styles) => ({
    ...styles,
    padding: 0,
  }),
  menuPortal: (styles) => ({
    ...styles,
    zIndex: 9999,
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: '#ffcdb6',
    color: '#1b1d1f',
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    paddingRight: 6,
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    ':hover': {
      backgroundColor: '#ca3423',
    },
    backgroundColor: '#ff5301',
    color: 'white',
    cursor: 'pointer',
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    '&:first-of-type': {
      backgroundColor: '#fff',
    },
    alignItems: 'center',
    backgroundColor: isSelected ? '#ffcdb6' : isFocused ? '#ffcdb6' : 'inherit',
    color: '#626262',
    columnGap: '0.5rem',
    cursor: 'pointer',
    display: 'flex',
    fontSize: '1.1rem',
    fontWeight: 600,
    padding: '0.8rem 1.6rem',
    width: '100%',
  }),
  placeholder: (styles) => ({
    ...styles,
    color: '#aeaeae',
    fontSize: '1.4rem',
    fontWeight: 500,
    zIndex: 1,
  }),
  valueContainer: (styles) => ({
    ...styles,
    height: '100%',
    padding: '0 0 0 1.2rem',
  }),
}

export interface Props extends Omit<SelectProps, 'isDisabled' | 'isMulti'> {
  checkboxInitialValue?: boolean
  disabled?: boolean
  error?: ReactNode
  isCheckboxDisabled?: boolean
  isReadOnly?: boolean
  label?: string
  labelPosition?: 'top' | 'left'
  menuClassName?: string
  onCheckboxChanged?: (checkboxValue: boolean) => void
  size?: Exclude<SIZES, 'large'>
  tooltip?: ReactNode
  withExcept?: boolean
  withOnly?: boolean
  withSelectAll?: boolean
}

const MultiSelectInput = ({
  checkboxInitialValue,
  className,
  closeMenuOnSelect = false,
  disabled,
  error,
  isCheckboxDisabled,
  isClearable = true,
  isReadOnly = false,
  label,
  labelPosition = 'top',
  menuClassName,
  menuIsOpen,
  menuPosition = 'absolute',
  onChange,
  onCheckboxChanged,
  options,
  optionType,
  placeholder = 'Select',
  required,
  size = 'medium',
  tooltip,
  value,
  withExcept,
  withOnly,
  withSelectAll = true,
  ...props
}: Props): ReactElement => {
  /** Local state */

  const [isMenuOpen, setIsMenuOpen] = useState(menuIsOpen)

  const classes = classNames('multi-select-input-wrapper', className, {
    //@ts-expect-error value is an array, not an object
    'has-value': !!value && value.length > 0,
    'input-component--erroneous': !!error,
    'multi-select-input-component--read-only': isReadOnly,
  })

  const isSelectAllSelected = useMemo(() => {
    return (value as IOptionWithIcon[] | undefined)?.length && options
      ? (value as IOptionWithIcon[]).length >= options.length
      : false
  }, [options, value])

  const selectAllOption = useMemo(
    () => ({ label: isSelectAllSelected ? 'Deselect all' : 'Select all', value: 'all' }),
    [isSelectAllSelected]
  )

  const filteredOptions: IOption[] | undefined = useMemo(
    () => (options && withSelectAll ? [selectAllOption, ...(options as IOption[])] : (options as IOption[])),
    [options, selectAllOption, withSelectAll]
  )

  /** Handlers */

  const handleMenuOpenClose = (isOpen: boolean) => () => setIsMenuOpen(isOpen)

  const handleChange = useCallback(
    (newValue: unknown, actionMeta: ActionMeta<unknown>) => {
      const { action, option } = actionMeta
      const castedOption = option as IOption

      if (onChange) {
        if (action === 'select-option' && castedOption.value === 'all' && !isSelectAllSelected) {
          onChange(options, actionMeta)
        } else if (action === 'select-option' && isSelectAllSelected) {
          onChange([], actionMeta)
        } else {
          onChange(newValue || [], actionMeta)
        }
      }
    },
    [isSelectAllSelected, options, onChange]
  )

  const handleOnlyButtonClick = useCallback(
    (value: IOption) => {
      onChange && onChange([value], { action: 'select-option', option: value })
    },
    [onChange]
  )

  const MenuCallback = useCallback(
    (menuProps: MenuProps) => Menu(menuProps, classNames('multi-select-input-menu', menuClassName)),
    [menuClassName]
  )

  const OptionsCallback = useCallback(
    (optionProps: OptionProps) =>
      Option(optionProps, props.components?.Option, withOnly ? handleOnlyButtonClick : undefined),
    [handleOnlyButtonClick, props.components?.Option, withOnly]
  )

  /** Render */

  return (
    <InputWrapper
      checkboxInitialValue={checkboxInitialValue}
      className={classes}
      disabled={disabled}
      isCheckboxDisabled={isCheckboxDisabled}
      label={label}
      labelPosition={labelPosition}
      onCheckboxChanged={onCheckboxChanged}
      required={required}
      size={size}
      tooltip={tooltip}
      withCheckbox={withExcept}
    >
      <Select
        {...props}
        className="react-select-container"
        classNamePrefix="react-select"
        closeMenuOnSelect={closeMenuOnSelect}
        components={{
          Control,
          Menu: MenuCallback,
          Option: OptionsCallback,
          ValueContainer,
        }}
        hideSelectedOptions={false}
        isClearable={!isReadOnly && isClearable && !!isMenuOpen}
        isDisabled={disabled}
        isMulti
        isOptionDisabled={() => isReadOnly}
        isSearchable={!isReadOnly}
        loadingMessage={() => 'Loading...'}
        menuIsOpen={isMenuOpen}
        menuPosition={menuPosition}
        menuShouldScrollIntoView={false}
        onChange={handleChange}
        onMenuClose={handleMenuOpenClose(false)}
        onMenuOpen={handleMenuOpenClose(true)}
        optionType={optionType}
        options={filteredOptions}
        placeholder={placeholder}
        styles={styles}
        value={value}
      />
    </InputWrapper>
  )
}

export default MultiSelectInput
